import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    useTheme,
    useMediaQuery,
    Chip,
    CircularProgress,
    Button,
} from '@mui/material';
import {
    People,
    Inventory,
    BrandingWatermark,
    Warning,
    CreditCard as CreditCardIcon,
    Style as GreenCardIcon,
    StyleOutlined as SilverCardIcon,
    StyleRounded as GoldCardIcon,
    ArrowForwardIos,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { callAPI, Method } from '../../commonFunctions/CommonFunctions';
import MetricCard from './Cards/MetricCard';
import WSDataGrid, { IColumns } from '../../Components/DataGrid/WSDataGrid';
import { useNavigate } from 'react-router-dom';

// Define TypeScript interfaces based on your data structure
interface CustomerSummary {
    total: number;
    new: number;
    growthRate: string;
}

interface ProductSummary {
    total: number;
    lowStock: number;
    lowStockPercentage: string;
}

interface BrandSummary {
    total: number;
    active: number;
}


interface CardSummary {
    total: number;
    active: number;
    activationRate: number; // e.g. "100.00"
    newThisMonth: number;
    expiringSoon: number;
    distribution: {
        [tier: string]: {
            count: number;
            percentage: string; // e.g. "50.00"
            totalBalance: number;
            totalPoints: number;
            averageBalance: number;
        };
    };
}

interface CustomerRegistration {
    _id: string;
    count: number;
}

interface TopProduct {
    name: string;
    brand: string;
    stock: number;
    sales: number;
}

interface TopBrand {
    _id: string;
    name: string;
    isActive: boolean;
    featured: boolean;
    productCount: number;
}

interface Offers {
    _id: string;
    name: string;
    startDate: Date;
    endDate: Date;
}

interface DashboardData {
    summary: {
        customers: CustomerSummary;
        products: ProductSummary;
        brands: BrandSummary;
        cards: CardSummary;
    };
    charts: {
        customerRegistrations: CustomerRegistration[];
        topProducts: TopProduct[];
        topBrands: TopBrand[];
        offers: Offers[];
    };
}


const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{ p: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
                <Typography variant="body2" fontWeight="bold">{payload[0].name}</Typography>
                <Typography variant="body2">Sales: {payload[0].value}</Typography>
                <Typography variant="body2">Stock: {payload[0].payload.stock}</Typography>
            </Paper>
        );
    }
    return null;
};

// Custom Legend for Pie Chart
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};


// Dashboard Component
const LoyaltyDashboard: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const response = await callAPI(`dashboards/reports/dashboard`, Method.GET, {});

                if (response.success) {
                    setDashboardData(response.data);
                } else {
                    setError('Failed to load dashboard data');
                }
            } catch (err) {
                setError('An error occurred while fetching data');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
                <CircularProgress color="primary" />
                <Typography variant="subtitle1">Loading dashboard data...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!dashboardData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Typography variant="h6">No data available</Typography>
            </Box>
        );
    }

    const { summary, charts } = dashboardData;

    // Format customer registration data for the chart
    const chartData = charts.customerRegistrations.map(item => ({
        date: item._id,
        registrations: item.count
    }));


    // const pieChartData = dashboardData?.charts.topProducts.map((product, index) => ({
    //     name: product.name,
    //     value: product.sales,
    //     stock: product.stock,
    //     brand: product.brand,
    //     sales: product.sales,
    //     fill: [
    //         theme.palette.primary.main,
    //         theme.palette.secondary.main,
    //         theme.palette.success.main,
    //         theme.palette.warning.main,
    //         theme.palette.error.main,
    //         theme.palette.info.main
    //     ][index % 6] // Cycle through colors if more than 6 products
    // }));

    // Calculate total sales for summary
    // const totalSales = pieChartData.reduce((sum, item) => sum + item.value, 0);

    const brandColumns: IColumns[] = [
        {
            header: "Brand Name",
            fieldName: "name",
            type: "default"
        },
        {
            header: "Product Count",
            fieldName: "productCount",
            type: "default"
        },

        {
            header: "Average Price",
            fieldName: "averagePrice",
        },
    ];

    const offerColumns: IColumns[] = [
        {
            header: "Offer Name",
            fieldName: "name",
            type: "default"
        },
        {
            header: "Start Date",
            fieldName: "startDate",
            type: "date"
        },

        {
            header: "End Date",
            fieldName: "endDate",
            type: "date"
        },
    ];

    return (
        <Box p={isMobile ? 1 : 3}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Dashboard
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Total Customers"
                        value={summary.customers.total}
                        subtitle={`${summary.customers.new} new registrations`}
                        icon={<People />}
                        trend="up"
                        trendValue={`${summary.customers.growthRate}%`}
                        delay={100}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Products"
                        value={summary.products.total}
                        subtitle={`${summary.products.lowStock} need restocking`}
                        icon={<Inventory />}
                        color={summary.products.lowStock > 0 ? theme.palette.warning.main : theme.palette.success.main}
                        trend={summary.products.lowStock > 0 ? 'down' : 'stable'}
                        trendValue={summary.products.lowStock > 0 ? `${summary.products.lowStockPercentage}%` : undefined}
                        delay={200}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Brands"
                        value={summary.brands.total}
                        subtitle={`${summary.brands.active} active`}
                        icon={<BrandingWatermark />}
                        trend="stable"
                        delay={300}
                    />
                </Grid> */}

                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Total Cards"
                        value={summary.cards.total}
                        subtitle={`${summary.cards.active} active (${summary.cards.activationRate}%)`}
                        icon={<CreditCardIcon />}
                        trend={summary.cards.activationRate > 90 ? "up" : summary.cards.activationRate < 70 ? "down" : "stable"}
                        trendValue={`${summary.cards.activationRate}%`}
                        delay={400}
                    />
                </Grid>

                {/* Green Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Green Cards"
                        value={summary.cards.distribution.Green?.count || 0}
                        subtitle={`${summary.cards.distribution.Green?.percentage || 0}% of total`}
                        icon={<GreenCardIcon sx={{ color: '#4caf50' }} />} // Green color
                        color="#4caf50"
                        trend="stable"
                        trendValue={`₹${summary.cards.distribution.Green?.averageBalance || 0}`}
                        delay={500}
                    />
                </Grid>

                {/* Silver Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Silver Cards"
                        value={summary.cards.distribution.Silver?.count || 0}
                        subtitle={`${summary.cards.distribution.Silver?.percentage || 0}% of total`}
                        icon={<SilverCardIcon sx={{ color: '#C0C0C0' }} />} // Silver/Grey color
                        color="#C0C0C0"
                        trend="up"
                        trendValue={`₹${summary.cards.distribution.Silver?.averageBalance || 0}`}
                        delay={600}
                    />
                </Grid>

                {/* Gold Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Gold Cards"
                        value={summary.cards.distribution.Gold?.count || 0}
                        subtitle={`${summary.cards.distribution.Gold?.percentage || 0}% of total`}
                        icon={<GoldCardIcon sx={{ color: '#FFBf00' }} />} // Gold color
                        color="#FFBf00"
                        trend="up"
                        trendValue={`₹${summary.cards.distribution.Gold?.averageBalance || 0}`}
                        delay={700}
                    />
                </Grid>
            </Grid>

            {/* Charts and Lists */}
            <Grid container spacing={3} mb={3}>
                {/* Customer Registration Chart */}
                <Grid item xs={12} >
                    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Customer Registrations
                        </Typography>
                        <Box height={300}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="registrations"
                                        stroke="#1976d2"
                                        strokeWidth={2}
                                        activeDot={{ r: 8 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Top Products */}
                {/* <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Top Products by Sales
                        </Typography>

                        {charts.topProducts.length > 0 ? (
                            <>
                                <Box sx={{ height: 100, mt: 2 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={renderCustomizedLabel}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>

                                
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        size="small"
                                        label={`Total Sales: ${totalSales}`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={`Products: ${pieChartData.length}`}
                                        color="secondary"
                                        variant="outlined"
                                    />
                                </Box>

                                
                                {pieChartData.length > 0 && (
                                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            🏆 Top Product
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: pieChartData[0].fill,
                                                fontSize: '0.875rem',
                                                mr: 1
                                            }}>
                                                {pieChartData[0].name.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {pieChartData[0].name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {pieChartData[0].sales} sales • {pieChartData[0].stock} in stock
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{
                                height: 250,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                color: 'text.secondary'
                            }}>
                                <Inventory sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                                <Typography variant="body2" align="center">
                                    No sales data available
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid> */}

                {/* Top Brands */}
                <Grid item xs={12} md={6}>

                    {/* Header with Title + View All */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Top Brands</Typography>

                        <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowForwardIos fontSize="small" />}
                            onClick={() => {
                                navigate('/brand/list')
                            }}
                        >
                            View All
                        </Button>
                    </Box>

                    {/* Data Grid */}
                    <WSDataGrid id="brandId" columns={brandColumns} data={charts.topBrands} />
                </Grid>

                <Grid item xs={12} md={6}>
                    {/* Header with Title + View All */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Offers</Typography>

                        <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowForwardIos fontSize="small" />}
                            onClick={() => {
                                navigate('/promotion/list')
                            }}
                        >
                            View All
                        </Button>
                    </Box>

                    {/* Data Grid */}
                    <WSDataGrid id="offerId" columns={offerColumns} data={charts.offers} />
                </Grid>

                {/* Low Stock Alert */}
                {/* {summary.products.lowStock > 0 && (
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2, bgcolor: '#fffde7' }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Warning color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6">Low Stock Alert</Typography>
                            </Box>
                            <Typography>
                                You have {summary.products.lowStock} product(s) with low inventory that may need restocking.
                            </Typography>
                        </Paper>
                    </Grid>
                )} */}
            </Grid>
        </Box>
    );
};

export default LoyaltyDashboard;